import apiService from '../Api'
import { appendEstablishments, IEstablishmentFields, IFaqFields, IProfessionalFields } from './EventService'
import {
  EventFormat,
  EventSeriesComplete,
  EventSeriesEditionsResponse,
  EventSeriesPickerItem,
  SeriesOverrideGroup,
  ITestimonialSlot
} from './types'

/** Modelo de estabelecimento da série, aplicado às edições por índice (ABRA-317). */
export interface ISeriesEstablishmentTemplateFields extends IEstablishmentFields {
  /** Indices (within the editions array) this establishment is applied to. */
  edition_indexes: number[]
}

export interface ISeriesTicketTemplateFields {
  id?: number
  type: 'PAID' | 'FREE'
  title: string
  quantity: number
  price: number
  sales_start: string
  sales_end: string
  max_per_purchase: number
  description: string
  visible: boolean
  fee_exempt: boolean
  is_child_ticket: boolean
  /** Os mesmos de `ITicketFields` (ABRA-319). */
  sell_at_door: boolean
  door_price: number | null
  /** Indices (within the editions array) this ticket is applied to. */
  edition_indexes: number[]
}

/**
 * Resultado de `create`/`update` da série. `skipped_child_ticket_editions` só vem do update: são
 * as edições em que "Ingresso Infantil" não mudou porque já houve check-in com aquele ingresso —
 * a API aplica o resto da atualização e preserva a flag nelas. Vazio quando nada foi pulado.
 */
export interface ISeriesWriteResult {
  id: number
  skipped_child_ticket_editions?: string[]
}

/** Corpo de `POST /event-series` e `PUT /event-series/{id}`: `{ message, data, skipped_child_ticket_editions }`. */
interface ISeriesWriteBody {
  data: { id: number }
  /** Fica no topo da resposta, irmã de `message`/`data` (contrato ABRA-238). O create sempre traz `[]`. */
  skipped_child_ticket_editions?: string[]
}

/**
 * Per-edition form fields. The "always per-edition" fields are sent for every
 * edition; the general-group fields are only sent when their group is listed in
 * `overrides` (otherwise the edition inherits the series default on the server).
 */
export interface IEditionFields {
  /** _key is a stable client-side id for React lists (not sent). */
  _key: string
  id?: number
  // Always per-edition
  name: string
  visible: boolean
  use_nfc: boolean
  date_start: string
  is_multiday: boolean
  date_end: string
  time_start: string
  time_end: string
  professionals: IProfessionalFields[]
  // Which general groups this edition customizes
  overrides: SeriesOverrideGroup[]
  // Override values (sent only for overridden groups)
  /** Custom URL: sent only when `slug` is overridden — otherwise the API derives it. */
  slug: string
  format: EventFormat | ''
  thumb: File | null
  thumbPreview: string | null
  thumb_wide: File | null
  thumbWidePreview: string | null
  video: File | null
  videoPreview: string | null
  remove_video: boolean
  copy: string
  target_audience: string
  topics: string
  use_term: boolean
  term: string | null
  use_testimonial: boolean
  testimonial_title: string | null
  testimonials: ITestimonialSlot[]
  active_campaign_lead_lists: string[]
  active_campaign_buyer_lists: string[]
  use_help_form: boolean
  types: number[]
  zipcode: string
  street: string
  number: string
  complement: string
  neighborhood: string
  city: string
  state: string
  latitude: number | null
  longitude: number | null
  place_id?: string | null
  place_name?: string | null
}

/** Series-level defaults shared by every edition, plus the editions and tickets. */
export interface IEventSeriesFields {
  name: string
  /** Base URL: the 1st edition uses it as is, the rest add `-{n}edicao`. */
  slug: string
  format: EventFormat | ''
  thumb: File | null
  thumb_wide: File | null
  video: File | null
  videoPreview: string | null
  remove_video: boolean
  copy: string
  target_audience: string
  topics: string
  min_installment_value: number | null
  use_term: boolean
  term: string | null
  use_testimonial: boolean
  testimonial_title: string | null
  testimonials: ITestimonialSlot[]
  active_campaign_lead_lists: string[]
  active_campaign_buyer_lists: string[]
  use_help_form: boolean
  types: number[]
  /** Cursos relacionados da série. Toda edição herda; não há override por edição. */
  course_ids: number[]
  zipcode: string
  street: string
  number: string
  complement: string
  neighborhood: string
  city: string
  state: string
  latitude: number | null
  longitude: number | null
  place_id?: string | null
  place_name?: string | null
  /** Series-level FAQs (additive with each event's own FAQs; not overridable per edition). */
  faqs: IFaqFields[]
  editions: IEditionFields[]
  ticket_templates: ISeriesTicketTemplateFields[]
  establishment_templates: ISeriesEstablishmentTemplateFields[]
}

export class EventSeriesService {
  async get(id: number): Promise<EventSeriesComplete> {
    return apiService.get(`/event-series/${id}`).then((response) => response.data)
  }

  async getEditions(id: number): Promise<EventSeriesEditionsResponse> {
    return apiService.get(`/event-series/${id}/editions`).then((response) => response.data)
  }

  /** Every series (id + name + editions count), for the attach/move picker. */
  async listForPicker(): Promise<EventSeriesPickerItem[]> {
    return apiService.get('/event-series').then((response) => response.data?.data ?? [])
  }

  /** Attach a standalone event to a series, or move it from its current series. */
  async attachEvent(seriesId: number, eventId: number): Promise<{ message: string }> {
    return apiService.post(`/event-series/${seriesId}/editions/${eventId}`).then((response) => response.data)
  }

  /** Remove an event from a series, turning it back into a standalone event. */
  async detachEvent(seriesId: number, eventId: number): Promise<{ message: string }> {
    return apiService.delete(`/event-series/${seriesId}/editions/${eventId}`).then((response) => response.data)
  }

  async create(form: IEventSeriesFields): Promise<ISeriesWriteResult> {
    const formData = this.buildFormData(form)

    return apiService
      .post('/event-series', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((response) => this.toWriteResult(response.data))
  }

  async update(id: number, form: IEventSeriesFields): Promise<ISeriesWriteResult> {
    const formData = this.buildFormData(form)
    formData.append('_method', 'PUT')

    return apiService
      .post(`/event-series/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((response) => this.toWriteResult(response.data))
  }

  async delete(id: number): Promise<void> {
    await apiService.delete(`/event-series/${id}`)
  }

  /** Mesma montagem para create e update: os dados da série mais a lista de edições puladas. */
  private toWriteResult(body: ISeriesWriteBody): ISeriesWriteResult {
    return { ...body.data, skipped_child_ticket_editions: body.skipped_child_ticket_editions ?? [] }
  }

  /**
   * Append testimonial slots (kept + newly uploaded) as parallel indexed arrays,
   * so the API can rebuild the ordered list. `prefix` is '' for the series level or
   * `editions[i]` for an edition override.
   */
  private appendTestimonialSlots(formData: FormData, prefix: string, slots: ITestimonialSlot[]): void {
    const key = (name: string, index: number) => (prefix ? `${prefix}[${name}][${index}]` : `${name}[${index}]`)

    // Send each slot under its own (visual) index rather than a filtered one, so a 422
    // from the API lines up with the slot the UI renders. Empty slots are simply skipped.
    slots.forEach((slot, index) => {
      if (slot.file) {
        formData.append(key('testimonial_videos', index), slot.file)
      } else if (slot.existingUrl) {
        formData.append(key('testimonial_existing', index), slot.existingUrl)
      }
    })
  }

  private buildFormData(form: IEventSeriesFields): FormData {
    const formData = new FormData()

    // --- Series-level defaults ---
    formData.append('name', form.name)
    formData.append('slug', form.slug)
    formData.append('format', form.format)
    if (form.video) {
      formData.append('video', form.video)
    }
    if (form.remove_video) {
      formData.append('remove_video', '1')
    }
    formData.append('copy', form.copy)
    formData.append('target_audience', form.target_audience)
    formData.append('topics', form.topics)

    if (form.thumb) {
      formData.append('thumb', form.thumb)
    }
    if (form.thumb_wide) {
      formData.append('thumb_wide', form.thumb_wide)
    }

    form.types.forEach((typeId) => formData.append('types[]', typeId.toString()))

    // Cursos relacionados (opcional). Array vazio não envia nada; a API limpa as
    // associações da série no update e repropaga para as edições.
    form.course_ids.forEach((courseId) => formData.append('course_ids[]', courseId.toString()))

    // A coluna é NOT NULL com default 5 — limpar o campo não pode virar null (o
    // banco rejeitaria), então volta para o próprio default, igual ao que a API já
    // faz na criação. Sempre enviado: sem isso, o campo nunca some depois de
    // definido, pois multipart não envia nada para um valor null.
    formData.append(
      'min_installment_value',
      form.min_installment_value !== null ? form.min_installment_value.toString() : '5'
    )

    formData.append('use_term', form.use_term ? '1' : '0')
    if (form.use_term) {
      formData.append('term', form.term ?? '')
    }

    formData.append('use_testimonial', form.use_testimonial ? '1' : '0')
    if (form.use_testimonial) {
      formData.append('testimonial_title', form.testimonial_title ?? '')
      this.appendTestimonialSlots(formData, '', form.testimonials)
    }

    // AC lists (optional). Empty arrays send nothing; the API clears them on update.
    form.active_campaign_lead_lists.forEach((listId) => formData.append('active_campaign_lead_lists[]', listId))
    form.active_campaign_buyer_lists.forEach((listId) => formData.append('active_campaign_buyer_lists[]', listId))

    formData.append('use_help_form', form.use_help_form ? '1' : '0')

    if (form.format === 'IN_PERSON' || form.format === 'HYBRID') {
      formData.append('zipcode', form.zipcode)
      formData.append('street', form.street)
      formData.append('number', form.number)
      formData.append('complement', form.complement)
      formData.append('neighborhood', form.neighborhood)
      formData.append('city', form.city)
      formData.append('state', form.state)
    }
    if (form.latitude !== null) {
      formData.append('latitude', form.latitude.toString())
    }
    if (form.longitude !== null) {
      formData.append('longitude', form.longitude.toString())
    }
    formData.append('place_id', form.place_id ?? '')
    formData.append('place_name', form.place_name ?? '')

    // --- FAQ (series-level; additive, never overridden per edition) ---
    // `faqs_provided` always sent so an emptied list clears the FAQs on the API.
    formData.append('faqs_provided', '1')
    form.faqs.forEach((faq, index) => {
      if (faq.id) {
        formData.append(`faqs[${index}][id]`, faq.id.toString())
      }
      formData.append(`faqs[${index}][question]`, faq.question)
      formData.append(`faqs[${index}][answer]`, faq.answer)
      formData.append(`faqs[${index}][position]`, index.toString())
    })

    // --- Editions ---
    form.editions.forEach((edition, i) => this.appendEdition(formData, edition, i))

    // --- Ticket templates ---
    form.ticket_templates.forEach((ticket, j) => this.appendTicketTemplate(formData, ticket, j))

    // --- Establishment templates (ABRA-317) ---
    // The marker tells the API this form manages them: without it (an older panel) the
    // editions' establishments are left alone. Always sent, so an emptied list clears them.
    formData.append('establishment_templates_provided', '1')
    appendEstablishments(formData, 'establishment_templates', form.establishment_templates)
    form.establishment_templates.forEach((template, i) => {
      template.edition_indexes.forEach((index) => {
        formData.append(`establishment_templates[${i}][edition_indexes][]`, index.toString())
      })
    })

    return formData
  }

  private appendEdition(formData: FormData, edition: IEditionFields, i: number): void {
    const prefix = `editions[${i}]`
    const has = (group: SeriesOverrideGroup) => edition.overrides.includes(group)

    if (edition.id) {
      formData.append(`${prefix}[id]`, edition.id.toString())
    }

    // Always per-edition
    formData.append(`${prefix}[name]`, edition.name)
    formData.append(`${prefix}[visible]`, edition.visible ? '1' : '0')
    formData.append(`${prefix}[use_nfc]`, edition.use_nfc ? '1' : '0')
    formData.append(`${prefix}[date_start]`, edition.date_start)
    formData.append(`${prefix}[is_multiday]`, edition.is_multiday ? '1' : '0')
    formData.append(`${prefix}[date_end]`, edition.is_multiday ? edition.date_end : '')
    formData.append(`${prefix}[time_start]`, edition.time_start)
    formData.append(`${prefix}[time_end]`, edition.time_end)

    edition.professionals.forEach((professional, k) => {
      const pPrefix = `${prefix}[professionals][${k}]`
      if (professional.teacher_id) {
        formData.append(`${pPrefix}[teacher_id]`, professional.teacher_id.toString())
      } else {
        formData.append(`${pPrefix}[title]`, professional.title)
        formData.append(`${pPrefix}[name]`, professional.name)
        formData.append(`${pPrefix}[bio]`, professional.bio)
        if (professional.thumb) {
          formData.append(`${pPrefix}[thumb]`, professional.thumb)
        }
      }
    })

    // Override flags
    edition.overrides.forEach((group) => formData.append(`${prefix}[overrides][]`, group))

    // Only a custom URL is sent: otherwise the API derives it from the series base.
    if (has('slug')) {
      formData.append(`${prefix}[slug]`, edition.slug)
    }

    if (has('format')) {
      formData.append(`${prefix}[format]`, edition.format)
    }
    if (has('types')) {
      edition.types.forEach((typeId) => formData.append(`${prefix}[types][]`, typeId.toString()))
    }
    if (has('thumb') && edition.thumb) {
      formData.append(`${prefix}[thumb]`, edition.thumb)
    }
    if (has('thumb_wide') && edition.thumb_wide) {
      formData.append(`${prefix}[thumb_wide]`, edition.thumb_wide)
    }
    if (has('video_url')) {
      if (edition.video) {
        formData.append(`${prefix}[video]`, edition.video)
      }
      if (edition.remove_video) {
        formData.append(`${prefix}[remove_video]`, '1')
      }
    }
    if (has('copy')) {
      formData.append(`${prefix}[copy]`, edition.copy)
    }
    if (has('target_audience')) {
      formData.append(`${prefix}[target_audience]`, edition.target_audience)
    }
    if (has('topics')) {
      formData.append(`${prefix}[topics]`, edition.topics)
    }
    if (has('term')) {
      formData.append(`${prefix}[use_term]`, edition.use_term ? '1' : '0')
      if (edition.use_term) {
        formData.append(`${prefix}[term]`, edition.term ?? '')
      }
    }
    if (has('testimonial')) {
      formData.append(`${prefix}[use_testimonial]`, edition.use_testimonial ? '1' : '0')
      if (edition.use_testimonial) {
        formData.append(`${prefix}[testimonial_title]`, edition.testimonial_title ?? '')
        this.appendTestimonialSlots(formData, prefix, edition.testimonials)
      }
    }
    if (has('active_campaign')) {
      edition.active_campaign_lead_lists.forEach((listId) =>
        formData.append(`${prefix}[active_campaign_lead_lists][]`, listId)
      )
      edition.active_campaign_buyer_lists.forEach((listId) =>
        formData.append(`${prefix}[active_campaign_buyer_lists][]`, listId)
      )
    }
    if (has('help_form')) {
      formData.append(`${prefix}[use_help_form]`, edition.use_help_form ? '1' : '0')
    }
    if (has('location')) {
      formData.append(`${prefix}[zipcode]`, edition.zipcode)
      formData.append(`${prefix}[street]`, edition.street)
      formData.append(`${prefix}[number]`, edition.number)
      formData.append(`${prefix}[complement]`, edition.complement)
      formData.append(`${prefix}[neighborhood]`, edition.neighborhood)
      formData.append(`${prefix}[city]`, edition.city)
      formData.append(`${prefix}[state]`, edition.state)
      if (edition.latitude !== null) {
        formData.append(`${prefix}[latitude]`, edition.latitude.toString())
      }
      if (edition.longitude !== null) {
        formData.append(`${prefix}[longitude]`, edition.longitude.toString())
      }
      formData.append(`${prefix}[place_id]`, edition.place_id ?? '')
      formData.append(`${prefix}[place_name]`, edition.place_name ?? '')
    }
  }

  private appendTicketTemplate(formData: FormData, ticket: ISeriesTicketTemplateFields, j: number): void {
    const prefix = `ticket_templates[${j}]`

    if (ticket.id) {
      formData.append(`${prefix}[id]`, ticket.id.toString())
    }
    formData.append(`${prefix}[type]`, ticket.type)
    formData.append(`${prefix}[title]`, ticket.title)
    formData.append(`${prefix}[quantity]`, ticket.quantity.toString())
    formData.append(`${prefix}[price]`, ticket.price.toString())
    formData.append(`${prefix}[sales_start]`, ticket.sales_start)
    formData.append(`${prefix}[sales_end]`, ticket.sales_end)
    formData.append(`${prefix}[max_per_purchase]`, ticket.max_per_purchase.toString())
    formData.append(`${prefix}[description]`, ticket.description)
    formData.append(`${prefix}[visible]`, ticket.visible ? '1' : '0')
    formData.append(`${prefix}[fee_exempt]`, ticket.fee_exempt ? '1' : '0')
    formData.append(`${prefix}[is_child_ticket]`, ticket.is_child_ticket ? '1' : '0')
    formData.append(`${prefix}[sell_at_door]`, ticket.sell_at_door ? '1' : '0')
    formData.append(`${prefix}[door_price]`, ticket.door_price !== null ? ticket.door_price.toString() : '')
    ticket.edition_indexes.forEach((idx) => formData.append(`${prefix}[edition_indexes][]`, idx.toString()))
  }
}
